// ============================================================================
// LOGINVIEW.JSX - Authentication UI Component
// Handles both Firebase auth and demo mode
// ============================================================================

import React, { useState } from 'react';
import { Shield, User, ChevronRight, Mail, Lock, UserPlus, AlertCircle, Loader2 } from 'lucide-react';
import {
  signIn,
  signUp,
  isFirebaseAuth,
  getDemoUsers,
  demoQuickLogin,
  getStoreMode,
  ROLES,
  ROLE_DISPLAY,
} from './core/index.js';

const AnimatedBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
  </div>
);

const LoginView = ({ onAuthSuccess, tenants = {} }) => {
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Only used in demo/local mode
  const tenantKeys = Object.keys(tenants);
  const [selectedTenant, setSelectedTenant] = useState(tenantKeys[0] || 'demo-clinic');

  // Determine mode - use getStoreMode for single source of truth
  const storeMode = getStoreMode();
  const isFirebase = storeMode === 'firebase';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (mode === 'signup') {
        result = await signUp(email, password, displayName || null);
      } else {
        result = await signIn(email, password);
      }

      if (result.success) {
        onAuthSuccess(result.profile);
      } else {
        setError(result.error || 'Authentication failed');
      }
    } catch (err) {
      setError(safeUserError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (role) => {
    setError('');
    setLoading(true);

    try {
      const result = await demoQuickLogin(role);
      if (result.success) {
        // For demo mode, override tenantId based on selection (except platform admin)
        const profile = {
          ...result.profile,
          tenantId: role === 'platform_admin' ? null : selectedTenant,
        };
        onAuthSuccess(profile);
      } else {
        setError(result.error || 'Demo login failed');
      }
    } catch (err) {
      setError(safeUserError(err));
    } finally {
      setLoading(false);
    }
  };

  // Demo mode quick login UI
  if (!isFirebase) {
    const demoUserList = [
      { role: 'gp_employee', name: 'Dr. Sarah Chen', email: 'gp@demo.local' },
      { role: 'specialist', name: 'Dr. James Wilson', email: 'specialist@demo.local' },
      { role: 'tenant_admin', name: 'Tenant Admin', email: 'admin@demo.local' },
      { role: 'platform_admin', name: 'Platform Admin', email: 'platform@demo.local' },
    ];

    return (
      <div className="min-h-screen relative flex items-center justify-center">
        <AnimatedBackground />
        <div className="relative z-10 w-full max-w-md px-4">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-semibold text-white mb-2">ReferralGate</h1>
            <p className="text-slate-400">Demo Mode - Select a user</p>
          </div>

          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6 space-y-6">
            {/* Tenant Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Tenant</label>
              <select
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
              >
                {Object.values(tenants).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {/* Demo Users */}
            <div className="space-y-3">
              {demoUserList.map((u) => (
                <button
                  key={u.role}
                  onClick={() => handleDemoLogin(u.role)}
                  disabled={loading}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border bg-slate-800/30 border-slate-700/50 hover:border-emerald-500/30 transition-colors disabled:opacity-50"
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center">
                    <User className="w-6 h-6 text-slate-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-white">{u.name}</p>
                    <p className="text-xs text-emerald-400">{ROLE_DISPLAY[u.role]?.name || u.role}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-600" />
                </button>
              ))}
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <p className="text-xs text-slate-500 text-center">
              Demo mode uses localStorage. Set VITE_STORE_MODE=firebase for real auth.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Firebase auth UI
  return (
    <div className="min-h-screen relative flex items-center justify-center">
      <AnimatedBackground />
      <div className="relative z-10 w-full max-w-md px-4">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-white mb-2">ReferralGate</h1>
          <p className="text-slate-400">
            {mode === 'signup' ? 'Create your account' : 'Sign in to continue'}
          </p>
        </div>

        <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">Display Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Dr. Jane Doe"
                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-slate-600"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-slate-600"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-slate-600"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/40 disabled:cursor-not-allowed rounded-xl text-white font-medium transition-colors"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : mode === 'signup' ? (
                <>
                  <UserPlus className="w-5 h-5" />
                  Create Account
                </>
              ) : (
                <>
                  <ChevronRight className="w-5 h-5" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {mode === 'signup' && (
            <div className="mt-6 pt-6 border-t border-slate-700/50">
              <p className="text-sm text-slate-400 text-center">
                Already have an account?{' '}
                <button
                  onClick={() => { setMode('login'); setError(''); }}
                  className="text-emerald-400 hover:text-emerald-300"
                >
                  Sign in
                </button>
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default LoginView;
